import { useCallback, useEffect } from 'react';

const useOverlayDebugTools = ({
    globalPlayersRef,
    waitingForNextMatchRef,
    setWaitingForNextMatch,
    clearAllPortraits,
    handlePlayerCreate,
    handlePlayerUpdate,
    removePlayer,
}) => {
    const sendDebugEvent = useCallback((eventData) => {
        const playerSlot = eventData.slot_position || eventData.player_slot || eventData.entity_index;

        if (eventData.event_name === 'player_controller_entity_created' || eventData.event_name === 'player_pawn_entity_created') {
            if (playerSlot) {
                handlePlayerCreate({
                    ...eventData,
                    player_slot: playerSlot,
                    slot_position: playerSlot
                });
            }
        } else if (
            eventData.event_name === 'player_controller_entity_updated' ||
            eventData.event_name === 'player_controller_entity_update'
        ) {
            if (playerSlot) {
                handlePlayerUpdate({
                    ...eventData,
                    player_slot: playerSlot,
                    slot_position: playerSlot
                });
            }
        } else if (
            eventData.event_name === 'player_controller_entity_deleted' ||
            eventData.event_name === 'player_pawn_entity_deleted'
        ) {
            if (playerSlot) {
                removePlayer(playerSlot);
            }
        } else {
            if (playerSlot) {
                handlePlayerUpdate({
                    ...eventData,
                    event_name: 'player_controller_entity_updated',
                    player_slot: playerSlot,
                    slot_position: playerSlot
                });
            }
        }
    }, [handlePlayerCreate, handlePlayerUpdate, removePlayer]);

    useEffect(() => {
        if (!import.meta.env.DEV) return;
        if (typeof window === 'undefined') return;

        if (!window.deadlockOverlayDebug) {
            window.deadlockOverlayDebug = {};
        }

        window.deadlockOverlayDebug.clearOverlay = () => {
            waitingForNextMatchRef.current = false;
            setWaitingForNextMatch(false);
            clearAllPortraits();
        };

        window.deadlockOverlayDebug.players = () => {
            const getLane = (player) => {
                const assignedLane = parseInt(player?.assigned_lane ?? '', 10);
                const originalLane = parseInt(player?.original_assigned_lane ?? '', 10);
                if (!isNaN(assignedLane) && assignedLane !== 0) return assignedLane;
                if (!isNaN(originalLane)) return originalLane;
                return 0;
            };

            const allPlayers = Object.entries(globalPlayersRef.current).map(([slot, p]) => ({
                slot: Number(slot),
                hero: p.hero_name || p.hero_id || '?',
                team: p.team,
                assigned_lane: p.assigned_lane,
                original_assigned_lane: p.original_assigned_lane,
                resolved_lane: getLane(p),
                player_slot: p.player_slot,
                entity_index: p.entity_index,
            }));

            const amber = allPlayers.filter(p => p.team === 'amber' || p.team === 2)
                .sort((a, b) => a.resolved_lane !== b.resolved_lane ? a.resolved_lane - b.resolved_lane : a.entity_index - b.entity_index);
            const sapphire = allPlayers.filter(p => p.team === 'sapphire' || p.team === 3)
                .sort((a, b) => a.resolved_lane !== b.resolved_lane ? a.resolved_lane - b.resolved_lane : a.entity_index - b.entity_index);

            console.log('=== AMBER (sorted) ===');
            console.table(amber);
            console.log('=== SAPPHIRE (sorted) ===');
            console.table(sapphire);
            console.log('=== RAW globalPlayers ===');
            console.log(JSON.parse(JSON.stringify(globalPlayersRef.current)));
            return { amber, sapphire, raw: globalPlayersRef.current };
        };

        return () => {
            if (window.deadlockOverlayDebug) {
                delete window.deadlockOverlayDebug.clearOverlay;
                delete window.deadlockOverlayDebug.players;
            }
        };
    }, [clearAllPortraits, globalPlayersRef, setWaitingForNextMatch, waitingForNextMatchRef]);

    useEffect(() => {
        if (!import.meta.env.DEV) return;
        if (typeof window === 'undefined') return;

        window.sendDebugEvent = sendDebugEvent;
        window.testPlayerUpdate = () => {
            const testEvent = {
                "entity_index": 3,
                "tick": 79644,
                "game_time": 1233.076,
                "event_type": "entity_update",
                "delta": "update",
                "entity_type": "player_controller",
                "pawn": 2233,
                "steam_id": 181456277,
                "steam_name": "john lang",
                "team": 2,
                "hero_id": 31,
                "hero_badge_xp": 0,
                "player_slot": 5,
                "rank": 0,
                "assigned_lane": 0,
                "original_assigned_lane": 1,
                "net_worth": 19430,
                "health_regen": null,
                "ultimate_trained": true,
                "kills": 4,
                "assists": 7,
                "deaths": 1,
                "denies": 8,
                "last_hits": 74,
                "hero_healing": 3775,
                "self_healing": 3775,
                "hero_damage": 12148,
                "objective_damage": 253,
                "ultimate_cooldown_end": 1166.0156,
                "upgrades": [
                    1710079648,
                    3776945997,
                    7409189,
                    84321454,
                    2603935618,
                    709540378,
                    334300056,
                    2121044373
                ],
                "event_name": "player_controller_entity_update"
            };
            sendDebugEvent(testEvent);
        };

        return () => {
            delete window.sendDebugEvent;
            delete window.testPlayerUpdate;
        };
    }, [sendDebugEvent]);
};

export default useOverlayDebugTools;

